import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ApiMetadataApiService, ComponentOwnership } from '@xpo-ltl/sdk-apimetadata';
import { ComponentOwnershipFields, ComponentOwnershipLabels } from '../../enums/component-ownership';
import {
  ApiComponentStatusCd,
  ApiComponentTypeCd,
  ApiEmployeeCustomerFacingCd,
  ApiOnPremGkeCd,
  ApiSuperComponentCd,
} from '@xpo-ltl/sdk-common';
import { DropdownBasic } from '../../ensemble-config/models/dropdown-basic.model';
import { ComponentOwnershipService } from '../../component-ownership/services/component-ownership.service';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { XpoSnackBar } from '@xpo-ltl/ngx-ltl-core/snack-bar';

@Component({
  selector: 'app-component-ownership-detail-dialog',
  templateUrl: './component-ownership-detail-dialog.component.html',
  styleUrls: ['./component-ownership-detail-dialog.component.scss'],
})
export class ComponentOwnershipDetailDialogComponent implements OnInit {
  componentOwnershipLabels = ComponentOwnershipLabels;
  componentOwnershipFields = ComponentOwnershipFields;

  formGroup: FormGroup;

  apiOnPremGkeCd = Object.values(ApiOnPremGkeCd);
  apiEmployeeCustomerFacingCd = Object.values(ApiEmployeeCustomerFacingCd);
  apiComponentTypeCd = Object.values(ApiComponentTypeCd);
  apiComponentStatusCd = Object.values(ApiComponentStatusCd);
  apiSuperComponentCd = Object.values(ApiSuperComponentCd);

  employees: DropdownBasic[] = [];
  techList: DropdownBasic[] = [];

  source = this.listEmployeesByLastName.bind(this);

  selectedAutocompleteValues: DropdownBasic[] = [];
  selectedTechList: DropdownBasic[] = [];

  component: ComponentOwnership = null;
  isEdit: boolean = false;

  selectedComponents: ComponentOwnership[] = [];
  isBulkUpdate: boolean = false;
  loading: boolean = false;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ComponentOwnershipDetailDialogComponent>,
    public apiMetadataApiService: ApiMetadataApiService,
    private componentOwnershipService: ComponentOwnershipService,
    private snackbar: XpoSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.employees = this.componentOwnershipService.getEmployees().map((emp) => {
      return {
        label: `${emp.firstName} ${emp.lastName}`,
        value: emp.id,
      };
    });

    this.componentOwnershipService.getTechnologyList().subscribe((techList) => (this.techList = techList));

    this.component = data?.component;
    this.selectedComponents = data?.selectedComponents || [];
    this.isBulkUpdate = !!this.selectedComponents.length;
    this.isEdit = !!this.component;
  }

  ngOnInit() {
    this.initializeForm();
    this.setAutocompleteValues();
    this.checkIfBulkUpdate();
  }

  checkIfBulkUpdate(): void {
    if (this.isBulkUpdate) {
      this.formGroup.reset();
    }
  }

  initializeForm(): void {
    const requiredValidators = this.isBulkUpdate ? [] : [Validators.required];

    this.formGroup = this.fb.group({
      [ComponentOwnershipFields.COMPONENT]: [
        this.isEdit ? this.component[ComponentOwnershipFields.COMPONENT] : '',
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.CODE]: [
        this.isEdit ? this.component[ComponentOwnershipFields.CODE] : '',
        [...requiredValidators, Validators.maxLength(3)],
      ],
      [ComponentOwnershipFields.VERSION]: [
        this.isEdit ? this.component[ComponentOwnershipFields.VERSION] : '',
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.ON_PREM_GKE]: [
        this.isEdit ? this.component[ComponentOwnershipFields.ON_PREM_GKE] : ApiOnPremGkeCd.GKE,
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.EMPLOYEE_CUSTOMER]: [
        this.isEdit ? this.component[ComponentOwnershipFields.EMPLOYEE_CUSTOMER] : ApiEmployeeCustomerFacingCd.EMPLOYEE,
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.COMPONENT_TYPE]: [
        this.isEdit ? this.component[ComponentOwnershipFields.COMPONENT_TYPE] : ApiComponentTypeCd.CORE,
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.TECHNOLOGY_LIST]: [
        this.isEdit ? this.component[ComponentOwnershipFields.TECHNOLOGY_LIST] : '',
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.TEAM]: [
        this.isEdit ? this.component[ComponentOwnershipFields.TEAM] : '',
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.LEADERSHIP_ID]: [
        this.isEdit ? this.component[ComponentOwnershipFields.LEADERSHIP_ID] : '',
      ],
      [ComponentOwnershipFields.PRODUCT_OWNER_ID]: [
        this.isEdit ? this.component[ComponentOwnershipFields.PRODUCT_OWNER_ID] : '',
      ],
      [ComponentOwnershipFields.TECH_LEAD_ID]: [
        this.isEdit ? this.component[ComponentOwnershipFields.TECH_LEAD_ID] : '',
      ],
      [ComponentOwnershipFields.ARCHITECT_ID]: [
        this.isEdit ? this.component[ComponentOwnershipFields.ARCHITECT_ID] : '',
      ],
      [ComponentOwnershipFields.PLATFORM_ENG_ID]: [
        this.isEdit ? this.component[ComponentOwnershipFields.PLATFORM_ENG_ID] : '',
      ],
      [ComponentOwnershipFields.STATUS_CD]: [
        this.isEdit ? this.component[ComponentOwnershipFields.STATUS_CD] : ApiComponentStatusCd.ACTIVE,
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.APPROVED_INTERFACE_LIST]: [
        this.isEdit ? this.component[ComponentOwnershipFields.APPROVED_INTERFACE_LIST] : '',
      ],
      [ComponentOwnershipFields.OWNED_TABLE_PREFIX_LIST]: [
        this.isEdit ? this.component[ComponentOwnershipFields.OWNED_TABLE_PREFIX_LIST] : '',
      ],
      [ComponentOwnershipFields.SUPER_COMPONENT_CD]: [
        this.isEdit ? this.component[ComponentOwnershipFields.SUPER_COMPONENT_CD] : '',
        [...requiredValidators],
      ],
      [ComponentOwnershipFields.COMPONENT_LEVEL]: [
        this.isEdit ? this.component[ComponentOwnershipFields.COMPONENT_LEVEL] : '',
        [Validators.max(99), Validators.min(0), ...requiredValidators],
      ],
      [ComponentOwnershipFields.SOX_COMPLIANT_IND]: [
        this.isEdit ? this.component[ComponentOwnershipFields.SOX_COMPLIANT_IND] : false,
      ],
      [ComponentOwnershipFields.AUTH_SCOPE_IND]: [
        this.isEdit ? this.component[ComponentOwnershipFields.AUTH_SCOPE_IND] : false,
      ],
      [ComponentOwnershipFields.SUPPORT_TYPE]: [
        this.isEdit ? this.component[ComponentOwnershipFields.SUPPORT_TYPE] : '',
      ],
      [ComponentOwnershipFields.DESCRIPTION]: [this.isEdit ? this.component[ComponentOwnershipFields.DESCRIPTION] : ''],
      [ComponentOwnershipFields.NOTES]: [this.isEdit ? this.component[ComponentOwnershipFields.NOTES] : ''],
    });

    if (this.isEdit) {
      const disabledFields = [
        ComponentOwnershipFields.COMPONENT,
        ComponentOwnershipFields.CODE,
        ComponentOwnershipFields.VERSION,
      ];

      disabledFields.forEach((field) => this.formGroup.get(field).disable());
    }
  }

  private setAutocompleteValues(): void {
    this.selectedAutocompleteValues[ComponentOwnershipFields.LEADERSHIP_ID] = null;
    this.selectedAutocompleteValues[ComponentOwnershipFields.PRODUCT_OWNER_ID] = null;
    this.selectedAutocompleteValues[ComponentOwnershipFields.TECH_LEAD_ID] = null;
    this.selectedAutocompleteValues[ComponentOwnershipFields.ARCHITECT_ID] = null;
    this.selectedAutocompleteValues[ComponentOwnershipFields.PLATFORM_ENG_ID] = null;

    if (this.isEdit) {
      this.selectedAutocompleteValues[ComponentOwnershipFields.LEADERSHIP_ID] = {
        label: this.component[ComponentOwnershipFields.LEADERSHIP_NAME],
        value: this.component[ComponentOwnershipFields.LEADERSHIP_ID],
      };
      this.selectedAutocompleteValues[ComponentOwnershipFields.PRODUCT_OWNER_ID] = {
        label: this.component[ComponentOwnershipFields.PRODUCT_OWNER_NAME],
        value: this.component[ComponentOwnershipFields.PRODUCT_OWNER_ID],
      };
      this.selectedAutocompleteValues[ComponentOwnershipFields.TECH_LEAD_ID] = {
        label: this.component[ComponentOwnershipFields.TECH_LEAD_NAME],
        value: this.component[ComponentOwnershipFields.TECH_LEAD_ID],
      };
      this.selectedAutocompleteValues[ComponentOwnershipFields.ARCHITECT_ID] = {
        label: this.component[ComponentOwnershipFields.ARCHITECT_NAME],
        value: this.component[ComponentOwnershipFields.ARCHITECT_ID],
      };
      this.selectedAutocompleteValues[ComponentOwnershipFields.PLATFORM_ENG_ID] = {
        label: this.component[ComponentOwnershipFields.PLATFORM_ENG_NAME],
        value: this.component[ComponentOwnershipFields.PLATFORM_ENG_ID],
      };

      this.selectedTechList = this.component[ComponentOwnershipFields.TECHNOLOGY_LIST]
        .split(',')
        .map((item) => item?.trim() || '')
        .filter((techName) => !!techName)
        .map((techName) => ({ label: techName, value: techName }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  save(): void {
    if (this.formGroup.valid) {
      if (this.isBulkUpdate) {
        this.bulkUpdate();
      } else {
        const component = this.formGroup.getRawValue();
        component[ComponentOwnershipFields.CODE] = component[ComponentOwnershipFields.CODE].toUpperCase();
        this.componentOwnershipService.upsertComponentOwnership(component).subscribe(() => this.dialogRef.close(true));
      }
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  listEmployeesByLastName(query: string): Observable<DropdownBasic[]> {
    return this.componentOwnershipService.listEmployeesByLastName(query).pipe(
      catchError(() => of([])),
      map((employees) =>
        employees.map((emp) => ({
          label: emp.getDisplayName(),
          value: emp.id,
        }))
      )
    );
  }

  private bulkUpdate(): void {
    this.loading = true;
    const form = this.formGroup.getRawValue();
    const result = this.selectedComponents.map((component) => {
      const newComponent = new ComponentOwnership();
      const keys = new Set([...Object.keys(component), ...Object.keys(form)]);

      keys.forEach((key) => {
        if (form[key] !== null && form[key] !== undefined && form[key] !== '') {
          newComponent[key] = form[key];
        } else {
          newComponent[key] = component[key];
        }
      });

      newComponent[ComponentOwnershipFields.CODE] = component[ComponentOwnershipFields.CODE]?.toUpperCase();
      return newComponent;
    });

    const apiCalls = result.map((component) => this.componentOwnershipService.upsertComponentOwnership(component));
    forkJoin(apiCalls).subscribe({
      next: () => this.dialogRef.close(true),
      error: () => {
        this.snackbar.error('An error occurred while updating the components');
        this.loading = false;
      },
    });
  }
}
